/**
 * Compact Blob file picker: container → folder → file, within one connection.
 *
 * Extracted as a component because the risk-assessment page needs it twice
 * (current + upcoming) and future AI tools will need it again. The pipeline
 * canvas has its own inline version; this is the standalone one, deliberately
 * kept small rather than generalised to cover the canvas's needs too.
 *
 * Flat-list navigation: `blobListFiles` returns a prefix listing, and folders
 * are derived from the names — Blob has no real directories, so a "folder" is
 * just a shared prefix.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Anchor,
  Breadcrumbs,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  IconCheckbox,
  IconFile,
  IconFolder,
  IconRefresh,
  IconSquare,
  IconX,
} from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { toastApiError } from "@/shared/ui/toast";
import { blobListContainers, blobListFiles } from "@/shared/api/integration-browse";

export interface BlobSelection {
  container: string;
  path: string;
}

/** `file` selects a blob to read; `folder` selects a destination to write into
 *  (the current prefix), which is what a save target needs. */
export type PickerMode = "file" | "folder";

interface Props {
  connectionId: string;
  label: string;
  value: BlobSelection | null;
  onChange: (selection: BlobSelection | null) => void;
  /** Multi-select. When supplied, clicking a file **toggles** its membership in
   *  `values` instead of replacing `value` — a configuration export is usually
   *  several files, and forcing one meant the operator silently lost the rest.
   *  `folder` mode ignores this: a save target is one destination. */
  values?: readonly BlobSelection[];
  onToggle?: (selection: BlobSelection) => void;
  /** Extensions the caller can actually read, e.g. [".pdf", ".xlsx"]. Others are
   *  shown greyed out rather than hidden — a user looking for a file they can
   *  see in Azure should find it here, and learn why it can't be picked. */
  acceptedExtensions?: readonly string[];
  /** Defaults to `file`. In `folder` mode existing files are listed for context
   *  but not selectable, and the selection tracks the folder you navigate into. */
  mode?: PickerMode;
}

function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot).toLowerCase() : "";
}

export function BlobFilePicker({
  connectionId,
  label,
  value,
  onChange,
  values,
  onToggle,
  acceptedExtensions,
  mode = "file",
}: Props) {
  // Multi-select is active only when the caller supplied both halves of the
  // contract, and never in folder mode.
  const multi = mode === "file" && values !== undefined && onToggle !== undefined;
  const getToken = useAccessToken();
  const [containers, setContainers] = useState<string[] | null>(null);
  const [container, setContainer] = useState<string | null>(
    value?.container ?? null,
  );
  const [prefix, setPrefix] = useState("");
  const [names, setNames] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const token = await getToken();
        const result = await blobListContainers(token, connectionId);
        setContainers(result.items.map((c) => c.name));
      } catch (err) {
        toastApiError("Could not list containers", err);
        setContainers([]);
      }
    })();
  }, [getToken, connectionId]);

  const load = useCallback(
    async (targetContainer: string, targetPrefix: string) => {
      setLoading(true);
      try {
        const token = await getToken();
        const result = await blobListFiles(
          token,
          connectionId,
          targetContainer,
          targetPrefix || undefined,
        );
        setNames(result.items.map((f) => f.name));
      } catch (err) {
        toastApiError("Could not list files", err);
        setNames([]);
      } finally {
        setLoading(false);
      }
    },
    [getToken, connectionId],
  );

  useEffect(() => {
    if (container) void load(container, prefix);
  }, [container, prefix, load]);

  // In folder mode the destination IS wherever you have navigated to, so it
  // updates as you browse rather than needing a separate click to confirm.
  useEffect(() => {
    if (mode === "folder" && container) onChange({ container, path: prefix });
    // `onChange` is intentionally omitted: callers pass an inline setter, so
    // including it would re-fire on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, container, prefix]);

  // Blob has no directories — a "folder" is the next path segment shared by
  // several names under the current prefix.
  const { folders, files } = useMemo(() => {
    const folderSet = new Set<string>();
    const fileList: string[] = [];
    for (const name of names ?? []) {
      const rest = name.slice(prefix.length);
      const slash = rest.indexOf("/");
      if (slash >= 0) folderSet.add(rest.slice(0, slash));
      else if (rest) fileList.push(rest);
    }
    return { folders: [...folderSet].sort(), files: fileList.sort() };
  }, [names, prefix]);

  const crumbs = useMemo(() => {
    const parts = prefix.split("/").filter(Boolean);
    return [
      { label: container ?? "", target: "" },
      ...parts.map((part, i) => ({
        label: part,
        target: parts.slice(0, i + 1).join("/") + "/",
      })),
    ];
  }, [prefix, container]);

  const onContainerChange = (next: string | null) => {
    setContainer(next);
    setPrefix("");
    setNames(null);
    // A selection in the old container is meaningless here.
    onChange(null);
  };

  const accepted = (name: string) =>
    !acceptedExtensions || acceptedExtensions.includes(extensionOf(name));

  return (
    <Stack gap="xs">
      <Text size="sm" fw={500}>
        {label}
      </Text>

      <Select
        placeholder="Choose a container"
        value={container}
        onChange={onContainerChange}
        data={containers ?? []}
        disabled={containers === null}
        searchable
        size="xs"
      />

      {container && (
        <Paper withBorder radius="sm" p="xs">
          <Group justify="space-between" align="center" mb="xs" wrap="nowrap">
            <Breadcrumbs separator="/" style={{ flexWrap: "wrap", minWidth: 0 }}>
              {crumbs.map((c) => (
                <Anchor
                  key={c.target}
                  size="xs"
                  onClick={() => setPrefix(c.target)}
                  style={{ cursor: "pointer" }}
                >
                  {c.label}
                </Anchor>
              ))}
            </Breadcrumbs>
            <Tooltip label="Refresh">
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={() => void load(container, prefix)}
                aria-label="Refresh file list"
              >
                <IconRefresh size={14} />
              </ActionIcon>
            </Tooltip>
          </Group>

          <ScrollArea.Autosize mah={200}>
            {loading && <Loader size="xs" />}
            {!loading && folders.length === 0 && files.length === 0 && (
              <Text size="xs" c="dimmed">
                This folder is empty.
              </Text>
            )}
            <Stack gap={2}>
              {folders.map((folder) => (
                <Group
                  key={`d:${folder}`}
                  gap="xs"
                  wrap="nowrap"
                  style={{ cursor: "pointer" }}
                  onClick={() => setPrefix(`${prefix}${folder}/`)}
                >
                  <IconFolder size={14} />
                  <Text size="xs">{folder}</Text>
                </Group>
              ))}
              {files.map((file) => {
                const full = `${prefix}${file}`;
                const selected = multi
                  ? (values ?? []).some(
                      (v) => v.path === full && v.container === container,
                    )
                  : value?.path === full && value.container === container;
                // Files are context in folder mode, not choices.
                const usable = mode === "file" && accepted(file);
                return (
                  <Group
                    key={`f:${file}`}
                    gap="xs"
                    wrap="nowrap"
                    style={{ cursor: usable ? "pointer" : "not-allowed" }}
                    onClick={() => {
                      if (!usable) return;
                      if (multi) onToggle?.({ container, path: full });
                      else onChange({ container, path: full });
                    }}
                  >
                    {multi ? (
                      selected ? (
                        <IconCheckbox size={14} />
                      ) : (
                        <IconSquare size={14} />
                      )
                    ) : (
                      <IconFile size={14} />
                    )}
                    <Text
                      size="xs"
                      fw={selected ? 700 : 400}
                      // Shown but not selectable, so a file visible in Azure is
                      // findable here and the reason it can't be used is clear.
                      c={usable ? undefined : "dimmed"}
                      td={usable ? undefined : "line-through"}
                    >
                      {file}
                    </Text>
                  </Group>
                );
              })}
            </Stack>
          </ScrollArea.Autosize>
        </Paper>
      )}

      {multi ? (
        (values ?? []).length > 0 ? (
          <Stack gap={2}>
            <Text size="xs" c="dimmed">
              {(values ?? []).length} file{(values ?? []).length === 1 ? "" : "s"} selected
            </Text>
            {(values ?? []).map((v) => (
              <Group key={`${v.container}/${v.path}`} gap={4} wrap="nowrap">
                <Text size="xs" c="dimmed" truncate style={{ flex: 1 }}>
                  {v.container}/{v.path}
                </Text>
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  color="red"
                  aria-label={`Remove ${v.path}`}
                  onClick={() => onToggle?.(v)}
                >
                  <IconX size={12} />
                </ActionIcon>
              </Group>
            ))}
          </Stack>
        ) : (
          <Text size="xs" c="dimmed">
            No files selected
          </Text>
        )
      ) : (
        value && (
          <Text size="xs" c="dimmed" truncate>
            {mode === "folder" ? "Saving into: " : "Selected: "}
            {value.container}/{value.path}
          </Text>
        )
      )}
    </Stack>
  );
}
